import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import RecentMatches from "../components/RecentMatches";
import MatchTabs from "../components/MatchTabs";
import UpcomingMatches from "../components/UpcomingMatches";
import SiteBanner from "../components/SiteBanner/SiteBanner";
import LiveMatchGrid from "./LivePage/LiveMatchGrid";
import { useLiveMatches } from "../services/LiveService";
import './PlayerPages/PlayerPage.css';

// To fix TypeScript error: "{}" is not assignable to "UpcomingMatchTableProps"
// (this is caused by a confusion between UpcomingMatches and UpcomingMatchTable)
// To solve this, we need to explicitly tell TypeScript that UpcomingMatches has no required props

const TABS = ['upcoming', 'live', 'results'];

// A match's whole life on one page: Kommende → Live (while OverPass streams it) → Resultater
// (straight away as "just finished", then as the parsed result once the demo is in).
const MatchesPage = () => {
    const { t } = useTranslation('pages');
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedTab = searchParams.get('tab');
    const [activeTab, setActiveTab] = useState(requestedTab && TABS.includes(requestedTab) ? requestedTab : 'upcoming');
    // Once the visitor (or a ?tab= link) has picked a tab, the page stops choosing for them.
    const tabChosen = useRef(!!requestedTab);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const { live, finished, loaded } = useLiveMatches();

    // Keep track of window resizing to handle mobile layout changes
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // On a match night, open on what's being played right now.
    useEffect(() => {
        if (tabChosen.current || !loaded || live.length === 0) return;
        tabChosen.current = true;
        setActiveTab('live');
    }, [loaded, live.length]);

    const changeTab = (tab: string) => {
        tabChosen.current = true;
        setActiveTab(tab);
        setSearchParams(tab === 'upcoming' ? {} : { tab }, { replace: true });
    };

    return (
        <div className="container">
            <SiteBanner />
            <div className={`text-center ${isMobile ? 'mb-2' : 'mb-3'}`}>
                <h1 className={isMobile ? 'fs-2' : ''}>{t('matches.title')}</h1>
                <p className={isMobile ? 'mb-2' : ''}>{t('matches.description')}</p>
            </div>
            <hr className={isMobile ? 'my-2' : ''} />
            <div className="container">
                <MatchTabs activeTab={activeTab} onTabChange={changeTab} liveCount={live.length} />
            </div>
            <hr className={isMobile ? 'my-2' : ''} />

            {activeTab === 'upcoming' && (
                <div>
                    <UpcomingMatches
                        onNoMatches={() => { if (!tabChosen.current) setActiveTab('results'); }}
                        onShowLive={() => changeTab('live')}
                    />
                </div>
            )}

            {activeTab === 'live' && (
                <div>
                    {live.length > 0
                        ? <LiveMatchGrid matches={live} />
                        : <p className="text-center">{t('live.noneLive')}</p>}
                </div>
            )}

            {activeTab === 'results' && (
                <div>
                    <RecentMatches compact={isMobile} pending={finished} />
                </div>
            )}
        </div>
    );
}

export default MatchesPage;
